/*
 * De API draait op een eigen hostnaam, niet onder /api van de site (ADR-0002).
 * Het adres komt uit de build; `VITE_API_BASE_URL` wordt in de pipeline gezet.
 * Blijft het leeg, dan valt het terug op een relatief pad, wat lokaal met een
 * proxy werkt.
 */
const basis = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

export interface BetaalStand {
  kenmerk: string
  status: 'nieuw' | 'betaald' | 'mislukt'
  bedragCenten: number
  /** Waar te betalen. Leeg als er al betaald is. */
  checkoutUrl: string | null
}

/** Onderscheid tussen "bestaat niet" en "het ging even mis" — dat leest anders. */
export class BetaallinkOnbekend extends Error {}

/**
 * Haalt de stand van een betaallink op en start zo nodig een verse betaling.
 */
export async function haalBetaalstand(
  token: string,
  signal?: AbortSignal,
): Promise<BetaalStand> {
  const antwoord = await fetch(
    `${basis}/api/betaal/${encodeURIComponent(token)}`,
    {
      signal,
    },
  )

  if (antwoord.status === 404) {
    throw new BetaallinkOnbekend()
  }

  if (!antwoord.ok) {
    throw new Error(`De server antwoordde met ${antwoord.status}.`)
  }

  return (await antwoord.json()) as BetaalStand
}

/** Wat de aanvraagfunctie terugstuurt als alles gelukt is. */
export interface AanvraagAntwoord {
  /** Dossiernummer, om te tonen en te noemen. */
  kenmerk: string
  /** Waar de browser naartoe moet om te betalen. */
  checkoutUrl: string
}

/** Eén afgekeurd veld, zoals de server het teruggeeft. */
export interface Veldfout {
  /** Naam zoals het formulier hem kent. Leeg betekent: over het formulier als geheel. */
  veld: string
  melding: string
}

/**
 * De server keurde de inzending af.
 *
 * Draagt de meldingen per veld, zodat ze op dezelfde plek onder hetzelfde veld
 * belanden als de meldingen van de controle in de browser (FR-07). Een melding
 * zonder veldnaam gaat over het formulier als geheel.
 *
 * Beide formulieren gebruiken hem: de server antwoordt met hetzelfde
 * `ValidatieAntwoord`, dus de frontend hoeft het maar op één manier te lezen.
 */
export class FormulierAfgekeurd extends Error {
  readonly fouten: readonly Veldfout[]

  constructor(fouten: readonly Veldfout[]) {
    super('Het formulier is afgekeurd.')
    this.name = 'FormulierAfgekeurd'
    this.fouten = fouten
  }
}

/**
 * De aanvraag staat vast, maar het aanmaken van de betaling mislukte (HTTP 502).
 *
 * Geen fout om weg te poetsen met "probeer het opnieuw": een tweede inzending
 * levert een tweede aanvraag op. De cliënt heeft de betaallink in zijn
 * ontvangstbevestiging staan en rondt het daarmee af (FR-08a, FR-11).
 */
export class BetalingNietGestart extends Error {
  readonly melding: string

  constructor(melding: string) {
    super(melding)
    this.name = 'BetalingNietGestart'
    this.melding = melding
  }
}

/**
 * Stuurt de aanvraag in en levert de checkout-URL terug (FR-07 t/m FR-09).
 *
 * Er gaat bewust geen bedrag mee. De prijs komt uitsluitend van de server; het
 * verzoekmodel daar kent geen bedragveld, en een test bewaakt dat.
 */
export async function verstuurAanvraag(
  velden: Record<string, string>,
  signal?: AbortSignal,
): Promise<AanvraagAntwoord> {
  const antwoord = await fetch(`${basis}/api/aanvraag`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(velden),
    signal,
  })

  if (antwoord.status === 400) {
    const { fouten } = (await antwoord.json()) as { fouten: Veldfout[] }
    throw new FormulierAfgekeurd(fouten ?? [])
  }

  if (antwoord.status === 502) {
    const { fouten } = (await antwoord.json()) as { fouten: Veldfout[] }
    throw new BetalingNietGestart(
      fouten?.[0]?.melding ??
        'Uw aanvraag is opgeslagen, maar het betalen lukte nu niet. U ontvangt ' +
          'een e-mail met een betaallink waarmee u het later kunt afronden.',
    )
  }

  if (!antwoord.ok) {
    throw new Error(`De server antwoordde met ${antwoord.status}.`)
  }

  return (await antwoord.json()) as AanvraagAntwoord
}

/**
 * Stuurt een bericht uit het contactformulier in (FR-20).
 *
 * Levert niets terug: gelukt is gelukt, en de bevestiging staat op het scherm.
 * Elke afloop die niet gelukt is, komt terug als <see cref="FormulierAfgekeurd" />
 * met de melding van de server — of dat nu een afgekeurd veld is (400), te veel
 * berichten achter elkaar (429), of een mail die niet weg kon (502). Voor de
 * bezoeker maakt dat verschil niets uit: het formulier blijft staan met wat hij
 * heeft ingevuld, en de melding zegt wat hij nu moet doen.
 *
 * Dat is bewust anders dan bij een aanvraag. Daar betekent een 502 dat de
 * aanvraag wél vaststaat en opnieuw verzenden een tweede aanvraag oplevert. Hier
 * is er niets vastgelegd, dus opnieuw proberen is precies goed.
 */
export async function verstuurContact(
  velden: Record<string, string>,
  signal?: AbortSignal,
): Promise<void> {
  const antwoord = await fetch(`${basis}/api/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(velden),
    signal,
  })

  if (antwoord.ok) return

  // 400, 429 en 502 dragen alle drie een ValidatieAntwoord. Iets anders is een
  // storing waar de server niets zinnigs over te zeggen heeft.
  if ([400, 429, 502].includes(antwoord.status)) {
    const { fouten } = (await antwoord.json()) as { fouten: Veldfout[] }
    throw new FormulierAfgekeurd(fouten ?? [])
  }

  throw new Error(`De server antwoordde met ${antwoord.status}.`)
}

/** € 150,00 — dezelfde notatie als op de knop van het formulier. */
export function bedragInEuro(centen: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format(centen / 100)
}
